import {
	Container,
	ContainerGutter,
	ContainerPadding,
	ReferenceBadge,
	Richtext,
	Text,
	TextAlignment,
	ThemeProvider,
	TokenTextAppearance,
	TokenTextColor,
	Breakpoints,
} from '@volkswagen-onehub/components-core';
import * as React from 'react';
import styled from 'styled-components';
import { useDisclaimers } from '../../../context/disclaimers-context';
import { PortalContext } from '../../../context/portal-context';
import { BenefitsCombined } from '../benefits';
import { BenefitsHelper } from '../../../helpers/benefits-helper';
import { handleScrollToDisclaimer } from '../../../helpers/disclaimers/disclaimers-utils';

const DivFlexColumn = styled.div`
	display: flex;
	flex-direction: column;
	flex-grow: 1;
`;

const VouchersCarInfo = styled.div`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	position: absolute;
	top: 16px;
	left: 16px;
	padding: 4px 8px;
	border-radius: 18px;
	background-color: rgba(0, 0, 0, 0.5);
`;

// const StatusIndicator = styled.div<{ status: string }>`
// 	background: ${(props: { status: string }) => props.status};
// 	border-radius: 50%;
// 	width: 16px;
// 	height: 16px;
// `;

const LimitedTimeBadge = styled(Container)<{ bgColor: string }>`
	background: ${({ bgColor }) => bgColor};
	border-radius: 100px;
	width: fit-content;
	flex-shrink: 0;
`;

const StyledReferenceBadge = styled(ReferenceBadge)`
	vertical-align: top;
	&::before {
		content: none;
	}
`;

const truncateToFirstParagraph = (text: string) => {
	const trimmed = text.trim();
	const match = /\r\n|\n|\\n/.exec(trimmed);
	return match ? trimmed.slice(0, match.index).trimEnd() : trimmed;
};

export const BenefitsDescription: React.FC<{
	benefit: BenefitsCombined;
	isPrimary?: boolean;
}> = ({ benefit, isPrimary }) => {
	const { portalService } = React.useContext(PortalContext);

	const isPromotion = benefit.type === 'promotion';

	const benefitCard = isPromotion ? benefit.data.creativity?.card : benefit.data.card;
	const benefitLegalText = isPromotion ? benefit.data.creativity?.card.disclaimer : benefit.data.legalText;

	const voucherDescription =
		!isPromotion && benefit.type === 'voucher'
			? (benefit.data.layer?.description ?? benefit.data.card.description)
			: undefined;

	const benefitDescription = isPromotion
		? benefitCard?.description
		: voucherDescription && truncateToFirstParagraph(voucherDescription);

	const benefitExpirationDate = isPromotion ? benefit.data.closeDate : benefit.data.expirationDate;
	const colorStatus = BenefitsHelper.getVoucherPromotionColor(benefitExpirationDate);
	const validityMessage = BenefitsHelper.getVoucherValidityMessage(benefitExpirationDate);

	const voucherVehicle =
		benefit.type === 'voucher' && benefit.data.vin
			? portalService.getVehicleOrOrderByVin(benefit.data.vin).getValue()
			: null;
	const voucherVehicleName = voucherVehicle
		? 'vgedModelGroup' in voucherVehicle
			? voucherVehicle.vgedModelGroup
			: voucherVehicle.vgedCommunicationModel
		: '';

	const { findBadge } = useDisclaimers();
	const badge = findBadge(benefitLegalText);

	const limitedTimeRowNoWrapBreakpoint = isPrimary ? Breakpoints.b560 : Breakpoints.b1600;
	return (
		<DivFlexColumn>
			{voucherVehicle && (
				<VouchersCarInfo>
					<ThemeProvider theme="main">
						<Text appearance={TokenTextAppearance.label150} color={TokenTextColor.inverted}>
							{voucherVehicleName}
						</Text>
					</ThemeProvider>
				</VouchersCarInfo>
			)}

			<Container wrap={'always'} gutter={ContainerGutter.dynamic0100}>
				{colorStatus && (
					<Container
						wrap={{ [Breakpoints.default]: 'always', [limitedTimeRowNoWrapBreakpoint]: 'never' }}
						verticalAlign={'center'}
						gutter={ContainerGutter.static150}
						shrinkContent
					>
						<LimitedTimeBadge bgColor={colorStatus}>
							<Container
								padding={{
									top: ContainerPadding.static100,
									right: ContainerPadding.static250,
									bottom: ContainerPadding.static100,
									left: ContainerPadding.static250,
								}}
							>
								<ThemeProvider theme="main">
									<Text
										appearance={TokenTextAppearance.label100}
										color={TokenTextColor.primary}
										textAlign={TextAlignment.left}
										bold
									>
										POR TIEMPO LIMITADO
									</Text>
								</ThemeProvider>
							</Container>
						</LimitedTimeBadge>
						{validityMessage && (
							<Text
								appearance={TokenTextAppearance.copy200}
								color={TokenTextColor.primary}
								textAlign={TextAlignment.left}
							>
								{validityMessage}
							</Text>
						)}
					</Container>
				)}
				<Container>
					<Text
						appearance={TokenTextAppearance.headline300}
						textAlign={TextAlignment.start}
						color={TokenTextColor.primary}
					>
						<Richtext markdown={benefitCard ? benefitCard.title : ''} />{' '}
						{badge && (
							<StyledReferenceBadge
								variant="large"
								onClick={() => {
									if (badge) handleScrollToDisclaimer(badge);
								}}
							>
								{badge.replace(/\.$/, '')}
							</StyledReferenceBadge>
						)}
					</Text>
				</Container>

				{isPrimary && benefitDescription && (
					<Container>
						<Text
							appearance={TokenTextAppearance.copy200}
							textAlign={TextAlignment.start}
							color={TokenTextColor.primary}
						>
							<Richtext markdown={benefitDescription} />
						</Text>
					</Container>
				)}
			</Container>
		</DivFlexColumn>
	);
};
